// เวลาละหมาดรายวัน — ดูค่าที่ระบบคำนวณ และแก้ให้ตรงปฏิทินที่โรงเรียนใช้
//
// ค่าตั้งต้นคำนวณจากตำแหน่งดวงอาทิตย์ ซึ่งมักต่างจากปฏิทินของสำนักจุฬาราชมนตรี
// อยู่ 1-2 นาที โรงเรียนต้องยึดปฏิทินที่ประกาศ หน้านี้จึงมีไว้แก้ให้ตรง
//
// แถวที่แก้ด้วยมือแล้วจะไม่ถูกคำนวณทับอีก (pt_source = 'manual')
// ถ้าต่างกันเท่ากันทุกวัน อย่ามานั่งแก้ทีละวัน 365 แถว — ตั้ง PRAYER_ADJUST_* ใน .env
// ครั้งเดียวแล้วมีผลกับทุกวันที่ยังไม่ถูกคำนวณ
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';

const API = process.env.REACT_APP_API || 'http://localhost:3002/api';

const PRAYERS = [
  { key: 'fajr', label: 'ซุบฮิ' },
  { key: 'dhuhr', label: 'ซุฮ์รี' },
  { key: 'asr', label: 'อัสรี' },
  { key: 'maghrib', label: 'มัฆริบ' },
  { key: 'isha', label: 'อิชาอ์' },
];

const todayISO = () => {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mm}-${dd}`;
};

const hhmm = (value) => String(value || '').slice(0, 5);

export default function PrayerTimes() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];

  const [date, setDate] = useState(todayISO());
  const [row, setRow] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`${API}/prayer/times`, { params: { date } });
      setRow(data);
      setDraft(Object.fromEntries(PRAYERS.map((p) => [p.key, hhmm(data[`pt_${p.key}`])])));
    } catch (error) {
      setRow(null);
      Swal.fire('อ่านเวลาละหมาดไม่สำเร็จ', error?.response?.data?.message || 'ลองใหม่อีกครั้ง', 'error');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  const dirty = row && PRAYERS.some((p) => draft[p.key] !== hhmm(row[`pt_${p.key}`]));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axiosInstance.put(`${API}/prayer/times/${date}`, {
        ...Object.fromEntries(PRAYERS.map((p) => [p.key, `${draft[p.key]}:00`])),
        note: 'แก้ให้ตรงปฏิทินที่โรงเรียนใช้',
      });
      setRow(data);
      setDraft(Object.fromEntries(PRAYERS.map((p) => [p.key, hhmm(data[`pt_${p.key}`])])));
      Swal.fire('บันทึกแล้ว', 'เวลานี้จะไม่ถูกคำนวณทับอีก', 'success');
    } catch (error) {
      Swal.fire('บันทึกไม่สำเร็จ', error?.response?.data?.message || 'ลองใหม่อีกครั้ง', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="mb-3">
          <h4 className="main-title mb-0">เวลาละหมาด</h4>
          <div className="fs-xs text-secondary">
            หน้าต่างรับแตะบัตรของแต่ละรอบคิดจากเวลาในหน้านี้
          </div>
        </div>

        <Card className="card-one mb-3">
          <Card.Body className="p-2 p-md-3">
            <Row className="g-2 align-items-end">
              <Col xs={12} md={4}>
                <Form.Label className="fs-xs text-secondary mb-1">วันที่</Form.Label>
                <Form.Control type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </Col>
              <Col xs={12} md="auto">
                <Button variant="outline-secondary" onClick={() => setDate(todayISO())}>วันนี้</Button>
              </Col>
              <Col className="text-md-end">
                {row && (
                  <Badge bg={row.pt_source === 'manual' ? 'primary' : 'secondary'}>
                    {row.pt_source === 'manual' ? 'แก้ด้วยมือแล้ว' : 'คำนวณโดยระบบ'}
                  </Badge>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {loading ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : row && (
          <Card className="card-one">
            <Card.Header><Card.Title as="h6">เวลาของวันที่เลือก</Card.Title></Card.Header>
            <Card.Body>
              <Row className="g-3">
                {PRAYERS.map((p) => (
                  <Col xs={6} md={4} lg key={p.key}>
                    <Form.Label className="fs-xs text-secondary mb-1">{p.label}</Form.Label>
                    <Form.Control
                      type="time"
                      value={draft[p.key] || ''}
                      onChange={(e) => setDraft((d) => ({ ...d, [p.key]: e.target.value }))}
                    />
                  </Col>
                ))}
              </Row>

              {row.pt_source === 'computed' && (
                <Alert variant="light" className="mt-3 mb-0 fs-sm">
                  ค่านี้ระบบคำนวณจากตำแหน่งดวงอาทิตย์ ถ้าต่างจากปฏิทินของโรงเรียน
                  <b> เท่ากันทุกวัน</b> อย่าแก้ทีละวัน ให้ตั้ง <code>PRAYER_ADJUST_*</code> ใน
                  <code> server/.env</code> ครั้งเดียวแล้วรีสตาร์ท จะมีผลกับทุกวันที่ยังไม่ถูกคำนวณ
                </Alert>
              )}

              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button
                  variant="secondary"
                  disabled={!dirty || saving}
                  onClick={() => setDraft(Object.fromEntries(PRAYERS.map((p) => [p.key, hhmm(row[`pt_${p.key}`])])))}
                >
                  ย้อนกลับ
                </Button>
                <Button variant="primary" disabled={!dirty || saving} onClick={save}>
                  {saving && <Spinner size="sm" animation="border" className="me-2" />}
                  บันทึก
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        <Footer />
      </div>
    </>
  );
}
